import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart, X, Flame, Users, Send, Shield, User, MapPin, Flag, ArrowLeft, Sparkles, Upload, Cake, Check } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-red/60";

const GENDERS = [{ v: "male", l: "Homme" }, { v: "female", l: "Femme" }, { v: "other", l: "Autre" }];
const LOOKING = [
  { v: "female", l: "Des femmes" }, { v: "male", l: "Des hommes" },
  { v: "both", l: "Les deux" }, { v: "friends", l: "Des amis" },
];
const COUNTRIES = [
  { v: "MG", l: "🇲🇬 Madagascar" }, { v: "FR", l: "🇫🇷 France" },
  { v: "RE", l: "🇷🇪 Réunion" }, { v: "MU", l: "🇲🇺 Maurice" }, { v: "BE", l: "🇧🇪 Belgique" },
  { v: "CA", l: "🇨🇦 Canada" }, { v: "OTHER", l: "🌍 Autre" },
];

export const DatingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("discover");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchModal, setMatchModal] = useState(null);
  const [superMeta, setSuperMeta] = useState({ free_left: 1, cost_vola: 50, vola_balance: 0 });

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/dating/profile");
      setProfile(data);
      if (data.super) setSuperMeta(data.super);
      setForm(data.exists && !data.banned ? {
        gender: data.gender || "", looking_for: data.looking_for || "",
        birthdate: data.birthdate || "", city: data.city || "", country: data.country || "MG",
        bio: data.bio || "", interests: data.interests || [],
        photos: data.photos || [], min_age: data.min_age || 18, max_age: data.max_age || 60,
        enabled: data.enabled,
      } : { gender: "", looking_for: "", birthdate: "", city: "", country: "MG",
            bio: "", interests: [], photos: [], min_age: 18, max_age: 60, enabled: true });
      if (data.enabled) {
        const [disc, mt] = await Promise.all([
          api.get("/dating/discover").catch(() => ({ data: [] })),
          api.get("/me/dating/matches").catch(() => ({ data: [] })),
        ]);
        setCandidates(disc.data || []);
        setMatches(mt.data || []);
      }
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { navigate("/login?next=/rencontres"); return; }
    loadAll();
    // eslint-disable-next-line
  }, [user]);

  const saveProfile = async () => {
    if (!form.gender || !form.looking_for) { toast.error("Sélectionne ton genre et qui tu cherches"); return; }
    if (!form.birthdate) { toast.error("Date de naissance requise"); return; }
    const minAge = superMeta.min_age || 18;
    const age = Math.floor((Date.now() - new Date(form.birthdate).getTime()) / 31557600000);
    if (age < minAge) { toast.error(`Tu dois avoir ${minAge} ans ou plus`); return; }
    try {
      await api.post("/me/dating/profile", form);
      toast.success("Profil enregistré ❤️");
      setEditing(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo trop lourde (max 5 Mo)"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", "dating");
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...(f.photos || []), data.url].slice(0, 6) }));
    } catch { toast.error("Échec upload"); }
  };

  const swipe = async (target_id, direction) => {
    // Garde-fou super-like : confirmation si payant (skipper en mode gratuit)
    if (direction === "super" && !superMeta.free_mode && superMeta.free_left <= 0) {
      if (superMeta.vola_balance < superMeta.cost_vola) {
        toast.error(`Solde insuffisant · ${superMeta.cost_vola} Vola requis`, {
          action: { label: "Gagner des Vola", onClick: () => navigate("/vola") },
        });
        return;
      }
      const ok = window.confirm(`Utiliser ${superMeta.cost_vola} Vola pour un super-like ? (solde : ${superMeta.vola_balance})`);
      if (!ok) return;
    }
    setCandidates((prev) => prev.filter((c) => c.user_id !== target_id));
    try {
      const { data } = await api.post("/dating/swipe", { target_id, direction });
      if (direction === "super" && !superMeta.free_mode) {
        if (data?.super_paid) {
          setSuperMeta((s) => ({ ...s, vola_balance: Math.max(0, s.vola_balance - s.cost_vola) }));
          toast.success(`Super-like envoyé · -${superMeta.cost_vola} Vola 💰`);
        } else {
          setSuperMeta((s) => ({ ...s, free_left: Math.max(0, s.free_left - 1) }));
        }
      }
      if (data?.match) {
        setMatchModal(data.match);
        loadAll();
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(`Vola insuffisant · besoin de ${e.response.data?.need_vola} Vola`, {
          action: { label: "Gagner des Vola", onClick: () => navigate("/vola") },
        });
      } else {
        toast.error("Erreur");
      }
      // Recharge la liste si erreur
      loadAll();
    }
  };

  const report = async (target_id) => {
    const reason = window.prompt("Motif (fake, harassment, inappropriate, minor, spam, other) :", "inappropriate");
    if (!reason) return;
    try {
      await api.post("/dating/report", { target_id, reason });
      toast.success("Signalement envoyé · utilisateur bloqué");
      setCandidates((prev) => prev.filter((c) => c.user_id !== target_id));
    } catch { toast.error("Erreur"); }
  };

  if (loading) return <div className="px-6 py-12 text-white/55">Chargement…</div>;

  // Profil banni
  if (profile?.banned) {
    return (
      <div className="px-4 md:px-12 py-16 max-w-xl mx-auto text-center" data-testid="dating-banned">
        <div className="rounded-3xl border border-brand-red/30 bg-brand-red/[0.04] p-10">
          <Shield size={36} className="mx-auto text-brand-red mb-3" />
          <h1 className="font-display text-2xl font-black mb-2">Profil suspendu</h1>
          <p className="text-sm text-white/65">Ton profil Rencontres a été suspendu suite à des signalements. Pour toute contestation, contacte <a className="text-brand-red hover:underline" href="mailto:contact@voamada.com">contact@voamada.com</a>.</p>
        </div>
      </div>
    );
  }

  // Onboarding
  if (!profile?.exists || !profile.enabled || editing) {
    return <ProfileForm form={form} setForm={setForm} save={saveProfile} onUpload={uploadPhoto}
      existing={profile?.exists} onCancel={profile?.exists ? () => setEditing(false) : null} />;
  }

  return (
    <div className="px-4 md:px-12 py-8 max-w-5xl mx-auto pb-32" data-testid="dating-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-5xl font-black inline-flex items-center gap-2">
            <Heart size={32} className="text-brand-red fill-brand-red" /> Rencontres
          </h1>
          <p className="text-xs text-white/55 mt-1">Rencontres sérieuses pour Africains et diaspora · 18+ · {matches.length} match(s)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow text-[11px] font-bold" data-testid="super-meta">
            <Flame size={12} /> {superMeta.free_mode ? "Super-likes illimités · gratuit" : superMeta.free_left > 0 ? `${superMeta.free_left} super gratuit` : `Super = ${superMeta.cost_vola} Vola`}
            {!superMeta.free_mode && <span className="text-white/55">· solde {superMeta.vola_balance}</span>}
          </span>
          <button onClick={() => setEditing(true)} className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="edit-profile-btn">
            <User size={12} /> Mon profil
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b border-white/10">
        <TabBtn active={tab === "discover"} onClick={() => setTab("discover")} label={`Découvrir (${candidates.length})`} testid="tab-discover" />
        <TabBtn active={tab === "matches"} onClick={() => setTab("matches")} label={`Mes matches (${matches.length})`} testid="tab-matches" />
      </div>

      {tab === "discover" && (
        candidates.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center">
            <Sparkles size={32} className="mx-auto text-brand-yellow mb-2" />
            <p className="font-bold">Aucun nouveau profil pour le moment.</p>
            <p className="text-sm text-white/55 mt-1">Reviens plus tard ou élargis tes critères (âge, pays) dans ton profil.</p>
          </div>
        ) : (
          <div className="relative max-w-md mx-auto" data-testid="swipe-stack">
            {candidates.slice(0, 3).map((c, idx) => (
              <SwipeCard key={c.user_id} profile={c} onSwipe={swipe} onReport={report} depth={idx} isTop={idx === 0} superMeta={superMeta} />
            ))}
          </div>
        )
      )}

      {tab === "matches" && (
        matches.length === 0 ? (
          <p className="text-center text-white/55 py-10 text-sm">Pas encore de match. Commence à swiper dans l'onglet Découvrir ❤️</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="matches-grid">
            {matches.map((m) => <MatchCard key={m.match_id} match={m} />)}
          </div>
        )
      )}

      {matchModal && <MatchModal match={matchModal} onClose={() => setMatchModal(null)} />}
    </div>
  );
};

const TabBtn = ({ active, onClick, label, testid }) => (
  <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition ${active ? "text-brand-red border-b-2 border-brand-red" : "text-white/55 hover:text-white"}`} data-testid={testid}>{label}</button>
);

const SwipeCard = ({ profile, onSwipe, onReport, depth, isTop, superMeta }) => {
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = profile.photos?.length ? profile.photos : (profile.avatar_url ? [profile.avatar_url] : []);
  const photo = photos[photoIdx] || "/favicon-512.png";
  return (
    <div
      className={`absolute inset-x-0 rounded-3xl overflow-hidden shadow-2xl transition-transform ${isTop ? "" : "pointer-events-none"}`}
      style={{ transform: `translateY(${depth * 8}px) scale(${1 - depth * 0.04})`, zIndex: 10 - depth, opacity: 1 - depth * 0.2, minHeight: 560 }}
      data-testid={`card-${profile.user_id}`}
    >
      <div className="relative h-[560px] bg-gradient-to-b from-gray-800 to-gray-900">
        <img src={photo} alt={profile.name} className="absolute inset-0 w-full h-full object-cover" />
        {photos.length > 1 && (
          <div className="absolute top-3 inset-x-3 flex gap-1">
            {photos.map((_, i) => (
              <div key={i} className={`flex-1 h-0.5 rounded-full ${i === photoIdx ? "bg-white" : "bg-white/30"}`} />
            ))}
          </div>
        )}
        {photos.length > 1 && (
          <>
            <button onClick={() => setPhotoIdx((i) => Math.max(0, i - 1))} className="absolute left-0 top-0 bottom-0 w-1/3" aria-label="prev" />
            <button onClick={() => setPhotoIdx((i) => Math.min(photos.length - 1, i + 1))} className="absolute right-0 top-0 bottom-0 w-1/3" aria-label="next" />
          </>
        )}
        <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black via-black/80 to-transparent">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-3xl font-black">{profile.name}</h2>
            <span className="text-2xl font-bold text-white/80">{profile.age}</span>
          </div>
          {profile.city && <p className="text-sm text-white/70 inline-flex items-center gap-1 mt-0.5"><MapPin size={12} /> {profile.city}</p>}
          {profile.bio && <p className="text-sm text-white/85 mt-2 line-clamp-3">{profile.bio}</p>}
          {profile.interests?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {profile.interests.slice(0, 5).map((i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/15 text-white">{i}</span>
              ))}
            </div>
          )}
        </div>
        {isTop && (
          <button onClick={() => onReport(profile.user_id)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white/70 hover:bg-brand-red/80 hover:text-white" title="Signaler" data-testid={`report-${profile.user_id}`}>
            <Flag size={14} />
          </button>
        )}
      </div>
      {isTop && (
        <div className="grid grid-cols-3 gap-2 p-4 bg-[#15100D] border-t border-white/10">
          <button onClick={() => onSwipe(profile.user_id, "pass")} className="py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 font-bold inline-flex items-center justify-center gap-2" data-testid={`pass-${profile.user_id}`}>
            <X size={18} /> Passer
          </button>
          <button onClick={() => onSwipe(profile.user_id, "super")} className="py-3 rounded-2xl bg-brand-yellow/20 hover:bg-brand-yellow/30 border border-brand-yellow/40 text-brand-yellow font-bold inline-flex flex-col items-center justify-center gap-0.5" data-testid={`super-${profile.user_id}`}>
            <span className="inline-flex items-center gap-1.5"><Flame size={18} /> Super</span>
            <span className="text-[9px] font-bold uppercase tracking-widest text-brand-yellow/80">
              {superMeta?.free_mode ? "gratuit" : superMeta?.free_left > 0 ? "1 gratuit" : `${superMeta?.cost_vola ?? 50} Vola`}
            </span>
          </button>
          <button onClick={() => onSwipe(profile.user_id, "like")} className="py-3 rounded-2xl bg-gradient-to-r from-brand-red to-brand-orange hover:brightness-110 text-white font-bold inline-flex items-center justify-center gap-2" data-testid={`like-${profile.user_id}`}>
            <Heart size={18} /> Like
          </button>
        </div>
      )}
    </div>
  );
};

const MatchCard = ({ match }) => {
  const o = match.other;
  const photo = o.photos?.[0] || o.avatar_url || "/favicon-512.png";
  return (
    <Link to={`/messages?to=${o.user_id}`} className="group relative rounded-2xl overflow-hidden aspect-[3/4] border border-white/10 hover:border-brand-red/60 transition" data-testid={`match-${match.match_id}`}>
      <img src={photo} alt={o.name} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black to-transparent">
        <p className="font-bold text-sm">{o.name} {o.age ? <span className="text-white/70">· {o.age}</span> : null}</p>
        {o.city && <p className="text-[11px] text-white/60 truncate">{o.city}</p>}
        <p className="text-[10px] text-brand-red font-bold uppercase tracking-widest mt-1 inline-flex items-center gap-1">
          <Send size={10} /> Envoyer un message
        </p>
      </div>
    </Link>
  );
};

const MatchModal = ({ match, onClose }) => {
  const o = match.other;
  const photo = o.photos?.[0] || o.avatar_url || "/favicon-512.png";
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-3xl border-2 border-brand-red bg-gradient-to-br from-brand-red/20 via-brand-orange/10 to-transparent p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <Heart size={48} className="mx-auto text-brand-red fill-brand-red mb-3 animate-pulse" />
        <h2 className="font-display text-4xl font-black text-white mb-1">C'est un match !</h2>
        <p className="text-white/70 text-sm mb-5">Toi et {o.name} vous êtes likés 💞</p>
        <img src={photo} alt={o.name} className="w-32 h-32 mx-auto rounded-full object-cover border-4 border-brand-red mb-5" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white/80 font-bold">Continuer</button>
          <Link to={`/messages?to=${o.user_id}`} onClick={onClose} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-brand-red to-brand-orange text-white font-bold inline-flex items-center justify-center gap-2">
            <Send size={14} /> Envoyer un message
          </Link>
        </div>
      </div>
    </div>
  );
};

const ProfileForm = ({ form, setForm, save, onUpload, existing, onCancel }) => {
  if (!form) return null;
  const upd = (p) => setForm((f) => ({ ...f, ...p }));
  const toggleInterest = (v) => {
    const has = form.interests.includes(v);
    upd({ interests: has ? form.interests.filter((x) => x !== v) : [...form.interests, v].slice(0, 10) });
  };
  const INTERESTS = ["Musique", "Cinéma", "Cuisine", "Voyage", "Sport", "Danse", "Art", "Lecture", "Nature", "Mode"];
  return (
    <div className="px-4 md:px-12 py-10 max-w-2xl mx-auto pb-32" data-testid="dating-onboarding">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-red/10 border border-brand-red/30 text-brand-red text-xs font-bold uppercase tracking-widest mb-3">
          <Heart size={11} /> Voamada Rencontres
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black">{existing ? "Mon profil" : "Crée ton profil"}</h1>
        <p className="text-sm text-white/55 mt-1">18 ans et plus uniquement · tes infos sont privées, tu peux désactiver à tout moment.</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label icon={Cake}>Date de naissance *</Label>
          <input type="date" value={form.birthdate || ""} onChange={(e) => upd({ birthdate: e.target.value })} className={inp} required data-testid="dating-birthdate" />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Je suis</Label>
            <select value={form.gender} onChange={(e) => upd({ gender: e.target.value })} className={inp} data-testid="dating-gender">
              <option value="">Choisir…</option>
              {GENDERS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
            </select>
          </div>
          <div>
            <Label>Je cherche</Label>
            <select value={form.looking_for} onChange={(e) => upd({ looking_for: e.target.value })} className={inp} data-testid="dating-looking">
              <option value="">Choisir…</option>
              {LOOKING.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label icon={MapPin}>Ville</Label>
            <input value={form.city} onChange={(e) => upd({ city: e.target.value })} placeholder="Antananarivo" className={inp} data-testid="dating-city" />
          </div>
          <div>
            <Label>Pays</Label>
            <select value={form.country} onChange={(e) => upd({ country: e.target.value })} className={inp} data-testid="dating-country">
              {COUNTRIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Âge min</Label>
            <input type="number" min="18" max="99" value={form.min_age} onChange={(e) => upd({ min_age: +e.target.value })} className={inp} />
          </div>
          <div>
            <Label>Âge max</Label>
            <input type="number" min="18" max="99" value={form.max_age} onChange={(e) => upd({ max_age: +e.target.value })} className={inp} />
          </div>
        </div>

        <div>
          <Label>Bio (max 2000)</Label>
          <textarea rows="4" value={form.bio} onChange={(e) => upd({ bio: e.target.value })} placeholder="Parle de toi, de ce que tu cherches…" className={inp} maxLength={2000} data-testid="dating-bio" />
        </div>

        <div>
          <Label>Centres d'intérêt (max 10)</Label>
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map((i) => {
              const active = form.interests.includes(i);
              return (
                <button key={i} type="button" onClick={() => toggleInterest(i)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-bold transition ${active ? "bg-brand-red/20 text-brand-red border-brand-red/40" : "bg-white/5 text-white/65 border-white/10 hover:bg-white/10"}`}>
                  {active && "✓ "}{i}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Photos (max 6)</Label>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {(form.photos || []).map((p, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button onClick={() => upd({ photos: form.photos.filter((_, j) => j !== i) })} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center">
                  <X size={12} />
                </button>
              </div>
            ))}
            {(form.photos?.length || 0) < 6 && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-white/15 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 text-white/45">
                <Upload size={20} />
                <span className="text-[10px] mt-1">Ajouter</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="dating-upload" />
              </label>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 p-3 rounded-xl bg-brand-red/5 border border-brand-red/20">
          <input type="checkbox" checked={form.enabled} onChange={(e) => upd({ enabled: e.target.checked })} className="accent-brand-red" data-testid="dating-enabled" />
          <span className="text-sm">Activer mon profil · les autres peuvent me voir dans Découvrir</span>
        </label>

        <p className="text-[11px] text-white/45 inline-flex items-center gap-1.5"><Shield size={11} /> En activant, j'accepte les CGU et je confirme avoir 18 ans ou plus.</p>

        <div className="flex gap-2 pt-4">
          <button onClick={save} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="dating-save"><Check size={14} /> {existing ? "Enregistrer" : "Créer mon profil"}</button>
          {onCancel && <button onClick={onCancel} className="btn-ghost text-sm">Annuler</button>}
        </div>
      </div>
    </div>
  );
};

const Label = ({ children, icon: Icon }) => (
  <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1 inline-flex items-center gap-1">
    {Icon && <Icon size={11} />} {children}
  </label>
);

export default DatingPage;
